/** Eshkol Galil Mizrahi (גליל מזרחי) — envelope, cluster polygon, map viewport. */
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point as turfPoint } from '@turf/helpers';
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import clusterGeoJsonRaw from '@/data/eshkol-galil-mizrahi.geojson?raw';

export const EASTERN_GALILEE_ENVELOPE = {
  south: 32.54,
  west: 35.41,
  north: 33.3,
  east: 35.79,
} as const;

/** @deprecated Use EASTERN_GALILEE_ENVELOPE */
export const EASTERN_GALILEE_BOUNDS = EASTERN_GALILEE_ENVELOPE;

export const ESHKOL_GOLDEN_POINTS = {
  beitHillel: { lat: 33.2075, lng: 35.6058, label: 'בית הילל' },
  metula: { lat: 33.28, lng: 35.58, label: 'מטולה' },
  katzrin: { lat: 32.99, lng: 35.57, label: 'קצרין' },
  majdalShams: { lat: 33.27, lng: 35.77, label: 'מג\'דל שמס' },
  kiryatShmona: { lat: 33.21, lng: 35.57, label: 'קריית שמונה' },
  center: { lat: 32.95, lng: 35.57, label: 'מרכז האשכול' },
} as const;

export const ESHKOL_REJECT_POINTS = {
  kinneretCenter: { lat: 32.82, lng: 35.58, label: 'מרכז הכנרת' },
  haifa: { lat: 32.79, lng: 34.99, label: 'חיפה' },
  telAviv: { lat: 32.07, lng: 34.78, label: 'תל אביב' },
  lebanonNorthMetula: { lat: 33.31, lng: 35.58, label: 'צפון מטולה (לבנון)' },
  lebanonEast: { lat: 33.25, lng: 35.85, label: 'מזרח הגבול (לבנון/סוריה)' },
  syriaEast: { lat: 33.27, lng: 35.82, label: 'מזרח הגבול (סוריה)' },
} as const;

export const CLUSTER_OUT_OF_BOUNDS_MESSAGE =
  'יש לסמן מיקום בתוך תחום אשכול גליל מזרחי';

/** Leaflet `maxBounds` / `LatLngBounds` corners: [[south, west], [north, east]] */
export const EASTERN_GALILEE_LEAFLET_BOUNDS: [[number, number], [number, number]] = [
  [EASTERN_GALILEE_ENVELOPE.south, EASTERN_GALILEE_ENVELOPE.west],
  [EASTERN_GALILEE_ENVELOPE.north, EASTERN_GALILEE_ENVELOPE.east],
];

export const EASTERN_GALILEE_CENTER: [number, number] = [32.95, 35.63];

export const EASTERN_GALILEE_DEFAULT_ZOOM = 11;
export const EASTERN_GALILEE_MIN_ZOOM = 10;
export const EASTERN_GALILEE_MAX_ZOOM = 17;

/** Wider viewport when the map panel is expanded. */
export const EASTERN_GALILEE_EXPANDED_MAX_ZOOM = 10;

const clusterCollection = JSON.parse(clusterGeoJsonRaw) as FeatureCollection<Polygon | MultiPolygon>;
const clusterFeature = clusterCollection.features[0] as Feature<Polygon | MultiPolygon>;

export function isWithinEnvelope(lat: number, lng: number): boolean {
  return (
    lat >= EASTERN_GALILEE_ENVELOPE.south &&
    lat <= EASTERN_GALILEE_ENVELOPE.north &&
    lng >= EASTERN_GALILEE_ENVELOPE.west &&
    lng <= EASTERN_GALILEE_ENVELOPE.east
  );
}

export function pointInEshkolCluster(lat: number, lng: number): boolean {
  if (!isWithinEnvelope(lat, lng)) return false;
  return booleanPointInPolygon(turfPoint([lng, lat]), clusterFeature);
}

export function isWithinEasternGalilee(lat: number, lng: number): boolean {
  return pointInEshkolCluster(lat, lng);
}

/** Every ring vertex must lie inside the cluster (blocks polygons spanning Lebanon/Syria). */
export function isGeometryWithinEasternGalilee(
  geometry: { type: string; coordinates: unknown } | null | undefined,
): boolean {
  if (!geometry || geometry.type !== 'Polygon') return false;
  const rings = geometry.coordinates;
  if (!Array.isArray(rings)) return false;
  for (const ring of rings) {
    if (!Array.isArray(ring)) return false;
    for (const coord of ring) {
      if (!Array.isArray(coord) || coord.length < 2) return false;
      const lng = Number(coord[0]);
      const lat = Number(coord[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
      if (!isWithinEasternGalilee(lat, lng)) return false;
    }
  }
  return true;
}

export function getClusterBoundaryGeoJson(): FeatureCollection<Polygon | MultiPolygon> {
  return clusterCollection;
}

export type SettlementCentroid = {
  id?: string;
  name: string;
  lat: number;
  lng: number;
  aliases?: string[];
};
